import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { preflightApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, Mic, Activity,
  Users, PhoneCall, ArrowLeft, Loader2, Phone,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePBX } from '../../context/PBXContext';

const CHECK = {
  PENDING: 'pending',
  RUNNING: 'running',
  PASS: 'pass',
  FAIL: 'fail',
  WARN: 'warn',
};

export default function WorkshopPreflightPage() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const pbx = usePBX();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  // Check results
  const [micCheck, setMicCheck] = useState(CHECK.PENDING);
  const [latencyCheck, setLatencyCheck] = useState(CHECK.PENDING);
  const [latencyMs, setLatencyMs] = useState(null);
  const [reachCheck, setReachCheck] = useState(CHECK.PENDING);
  const [testCallCheck, setTestCallCheck] = useState(CHECK.PENDING);
  const [numberHealth, setNumberHealth] = useState(null);
  const [numberHealthLoading, setNumberHealthLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(false);

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const data = await preflightApi.getState(exerciseId);
      setState(data);
    } catch (e) {
      toast.error(`Failed to load pre-flight state: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  useEffect(() => { loadState(); }, [loadState]);

  // Check 1: Microphone
  const checkMicrophone = async () => {
    setMicCheck(CHECK.RUNNING);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicCheck(CHECK.PASS);
      return true;
    } catch (err) {
      console.error('Mic check failed:', err);
      setMicCheck(CHECK.FAIL);
      return false;
    }
  };

  // Check 2: Socket.IO Latency
  const checkLatency = async () => {
    setLatencyCheck(CHECK.RUNNING);
    // Pull current latency from the connection record
    try {
      const data = await preflightApi.getState(exerciseId);
      const myUid = data.connections.latencies && Object.keys(data.connections.latencies)[0];
      const ms = myUid ? data.connections.latencies[myUid] : null;
      setLatencyMs(ms);
      if (ms == null) {
        setLatencyCheck(CHECK.WARN);
      } else if (ms < 150) {
        setLatencyCheck(CHECK.PASS);
      } else if (ms < 400) {
        setLatencyCheck(CHECK.WARN);
      } else {
        setLatencyCheck(CHECK.FAIL);
      }
    } catch {
      setLatencyCheck(CHECK.FAIL);
    }
  };

  // Check 3: Peer Reachability
  const checkPeerReachability = async (currentState) => {
    setReachCheck(CHECK.RUNNING);
    const expected = currentState?.connections?.expected_count || 0;
    const connected = currentState?.connections?.connected_count || 0;
    if (expected === 0) {
      setReachCheck(CHECK.WARN);
      return;
    }
    const ratio = connected / expected;
    if (ratio >= 1) setReachCheck(CHECK.PASS);
    else if (ratio >= 0.7) setReachCheck(CHECK.WARN);
    else setReachCheck(CHECK.FAIL);
  };

  // Check 4: Test Call
  const checkTestCall = async (currentState) => {
    setTestCallCheck(CHECK.RUNNING);
    const exts = (currentState?.pbx_numbers || []).filter((n) => n.assigned_user_id).map((n) => n.extension);
    if (exts.length < 2) {
      setTestCallCheck(CHECK.WARN);
      return;
    }
    try {
      const r = await preflightApi.testCall(exerciseId, exts[0], exts[1]);
      setTestCallCheck(r.test_id ? CHECK.PASS : CHECK.WARN);
    } catch (e) {
      setTestCallCheck(CHECK.FAIL);
    }
  };

  const runAllChecks = async () => {
    setRunning(true);
    abortRef.current = false;
    setProgress(0);
    setMicCheck(CHECK.PENDING);
    setLatencyCheck(CHECK.PENDING);
    setReachCheck(CHECK.PENDING);
    setTestCallCheck(CHECK.PENDING);

    await checkMicrophone();
    setProgress(20);
    if (abortRef.current) return setRunning(false);

    await checkLatency();
    setProgress(40);
    if (abortRef.current) return setRunning(false);

    const fresh = await preflightApi.getState(exerciseId);
    setState(fresh);
    await checkPeerReachability(fresh);
    setProgress(60);
    if (abortRef.current) return setRunning(false);

    await checkTestCall(fresh);
    setProgress(80);
    if (abortRef.current) return setRunning(false);

    await runNumberHealth();
    setProgress(100);
    setRunning(false);
    toast.success('Pre-flight checks complete');
  };

  const runNumberHealth = async () => {
    setNumberHealthLoading(true);
    try {
      const r = await preflightApi.numberHealth(exerciseId);
      setNumberHealth(r);
    } catch (e) {
      toast.error(`Number health check failed: ${e.message || e}`);
    } finally {
      setNumberHealthLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="preflight-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/exercises/${exerciseId}`)} data-testid="preflight-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Workshop Pre-flight</h1>
            <p className="text-sm text-muted-foreground">
              {state?.exercise?.name && <span>Exercise: <span className="font-medium">{state.exercise.name}</span> · </span>}
              4-step diagnostic before going live
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadState} disabled={loading || running} data-testid="preflight-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={runAllChecks} disabled={running} data-testid="preflight-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
            {running ? 'Running...' : 'Run All Checks'}
          </Button>
        </div>
      </div>

      {running && <Progress value={progress} className="h-2" />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CheckCard
          title="Microphone Permission"
          icon={<Mic className="w-5 h-5" />}
          status={micCheck}
          description="Verifies the browser has microphone access for WebRTC."
          detail={pbx?.micPermission ? `Browser permission: ${pbx.micPermission}` : null}
          testId="check-card-mic"
        />
        <CheckCard
          title="Socket.IO Latency"
          icon={<Activity className="w-5 h-5" />}
          status={latencyCheck}
          description="Round-trip time to the server. < 150ms ideal, > 400ms problematic."
          detail={latencyMs != null ? `${latencyMs} ms RTT` : null}
          testId="check-card-latency"
        />
        <CheckCard
          title="Peer Reachability"
          icon={<Users className="w-5 h-5" />}
          status={reachCheck}
          description="How many assigned participants are currently connected."
          detail={state ? `${state.connections.connected_count} of ${state.connections.expected_count} connected` : null}
          testId="check-card-reach"
        />
        <CheckCard
          title="Test Call"
          icon={<PhoneCall className="w-5 h-5" />}
          status={testCallCheck}
          description="Initiates a synthetic call between two assigned extensions."
          detail={state ? `${(state.pbx_numbers || []).filter((n) => n.assigned_user_id).length} extensions assigned` : null}
          testId="check-card-testcall"
        />
      </div>

      {state && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Participant Readiness</CardTitle>
          </CardHeader>
          <CardContent>
            {state.participants.length === 0 ? (
              <p className="text-sm text-muted-foreground">No participants assigned yet.</p>
            ) : (
              <div className="space-y-2">
                {state.participants.map((p) => (
                  <div
                    key={p.user_id}
                    className="flex items-center justify-between p-2.5 rounded-md border bg-card"
                    data-testid={`participant-row-${p.user_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${p.online ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-400'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{p.email} · {(p.roles || []).join(', ')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {p.has_extension ? (
                        <Badge variant="secondary" className="font-mono text-xs">ext {p.extension}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">no extension</Badge>
                      )}
                      {p.latency_ms != null && (
                        <Badge variant="outline" className="text-xs font-mono">{p.latency_ms}ms</Badge>
                      )}
                      {!p.online && <Badge variant="outline" className="text-xs text-red-600 border-red-300">offline</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {state && state.warnings && state.warnings.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-5 h-5" /> Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {state.warnings.map((w, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {state && state.telnyx_configs && state.telnyx_configs.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="w-5 h-5" /> Number Health Check
              {numberHealth?.summary && (
                <span className="text-xs font-normal text-muted-foreground">
                  ({numberHealth.summary.pass}/{numberHealth.summary.total} pass
                  {numberHealth.summary.warn > 0 && `, ${numberHealth.summary.warn} warn`}
                  {numberHealth.summary.fail > 0 && `, ${numberHealth.summary.fail} fail`})
                </span>
              )}
            </CardTitle>
            <Button size="sm" variant="outline" onClick={runNumberHealth} disabled={numberHealthLoading} data-testid="number-health-run-btn">
              {numberHealthLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="ml-1.5">Run</span>
            </Button>
          </CardHeader>
          <CardContent>
            {!numberHealth ? (
              <p className="text-sm text-muted-foreground">Click Run to verify each Telnyx number's connection, webhook URL, and routing target reachability.</p>
            ) : numberHealth.checks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No numbers assigned.</p>
            ) : (
              <div className="space-y-2">
                {numberHealth.checks.map((c) => (
                  <NumberHealthCard key={c.config_id} item={c} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function NumberHealthCard({ item }) {
  const STATUS_BG = {
    pass: 'border-emerald-300 dark:border-emerald-700',
    warn: 'border-amber-300 dark:border-amber-700',
    fail: 'border-red-300 dark:border-red-700',
    skip: 'border-zinc-300 dark:border-zinc-700',
  };
  const STATUS_ICON = {
    pass: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
    warn: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    fail: <XCircle className="w-4 h-4 text-red-500" />,
    skip: <span className="text-zinc-400 text-xs">○</span>,
  };
  return (
    <div className={`border rounded-lg p-3 ${STATUS_BG[item.overall_status]}`} data-testid={`number-health-${item.config_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {STATUS_ICON[item.overall_status]}
          <span className="font-mono text-sm font-medium truncate">{item.phone_number}</span>
          <span className="text-xs text-muted-foreground truncate">— {item.label} · {item.routing_mode}</span>
        </div>
      </div>
      <div className="space-y-1 pl-6">
        {item.checks.map((c, i) => (
          <div key={i} className="flex items-start gap-2 text-xs">
            {STATUS_ICON[c.status]}
            <div className="flex-1 min-w-0">
              <span className="font-medium">{c.label}</span>
              {c.reason && <span className="text-muted-foreground ml-2">— {c.reason}</span>}
              {c.details && Array.isArray(c.details) && c.details.length > 0 && (
                <ul className="mt-0.5 ml-3 list-disc">
                  {c.details.map((d, j) => (
                    <li key={j} className="text-muted-foreground">ext {d.extension}: {d.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function CheckCard({ title, icon, status, description, detail, testId }) {
  const STATUS_UI = {
    [CHECK.PENDING]: { color: 'text-zinc-400', bg: 'bg-zinc-100 dark:bg-zinc-800', icon: '○', label: 'Not run' },
    [CHECK.RUNNING]: { color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/40', icon: <Loader2 className="w-4 h-4 animate-spin" />, label: 'Running' },
    [CHECK.PASS]: { color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/40', icon: <CheckCircle2 className="w-4 h-4" />, label: 'Pass' },
    [CHECK.FAIL]: { color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-950/40', icon: <XCircle className="w-4 h-4" />, label: 'Fail' },
    [CHECK.WARN]: { color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/40', icon: <AlertTriangle className="w-4 h-4" />, label: 'Warning' },
  };
  const ui = STATUS_UI[status] || STATUS_UI[CHECK.PENDING];

  return (
    <Card data-testid={testId} className={status === CHECK.FAIL ? 'border-red-300 dark:border-red-700' : status === CHECK.WARN ? 'border-amber-300 dark:border-amber-700' : ''}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${ui.bg} ${ui.color}`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">{title}</h3>
              <span className={`flex items-center gap-1 text-xs font-medium ${ui.color}`}>
                {typeof ui.icon === 'string' ? <span>{ui.icon}</span> : ui.icon}
                {ui.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            {detail && <p className="text-xs font-mono mt-1.5">{detail}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
